import { HttpErrorResponse } from '@angular/common/http';
import { AfterViewInit, ChangeDetectorRef, Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ConfirmationService } from 'primeng/api';
import { DropdownChangeEvent } from 'primeng/dropdown';
import { OrgConfigs } from '../../shared/models/org-configs';
import { NotificationService } from '../../shared/service/notification.service';
import CommonUtils from '../../shared/utils/common.utils';
import { Chapter } from '../models/chapter';
import { County } from '../models/county';
import { Registration } from '../models/registration';
import { State } from '../models/state';
import { RegistrationService } from '../registration.service';

@Component({
  selector: 'app-exhibitor-info',
  templateUrl: './exhibitor-info.component.html',
  styleUrl: './exhibitor-info.component.scss'
})
export class ExhibitorInfoComponent implements OnChanges, AfterViewInit {


  showErrorMessage = CommonUtils.showErrorMessage;

  @Input() states: State[] = [];
  @Input() counties: County[] = [];
  @Input() registration!: Registration;
  @Input() orgId: number = 0;
  @Input() orgConfigs: OrgConfigs | undefined;

  today = new Date();
  minDate = new Date(2002, 0, 1);
  maxDate = new Date(2017, 11, 31);
  formGroup: FormGroup;
  showSSN: boolean = false;
  showDialog: boolean = false;
  previousBirthDate: Date | undefined;
  chapters: Chapter[] = [];

  constructor(
    private readonly fb: FormBuilder,
    private readonly changeDetector: ChangeDetectorRef,
    private readonly registrationService: RegistrationService,
    private readonly notificationService: NotificationService,
    private readonly confirmationService: ConfirmationService) {

    this.formGroup = this.fb.group({
      orgId: [null],
      firstName: [null, [Validators.required]],
      lastName: [null, [Validators.required]],
      exhibitorDOB: [null, [Validators.required]],
      email: [null, [Validators.required, Validators.pattern(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/)]],
      phone: [null, [Validators.required]],
      address: [null, [Validators.required]],
      city: [null, [Validators.required]],
      state: [null, [Validators.required]],
      zipCode: [null, [Validators.required, Validators.minLength(5), Validators.maxLength(5)]],
      county: [null, [Validators.required]],
      chapter: [null, [Validators.required]]
    });
  }

  ngAfterViewInit(): void {
    this.formGroup.get('exhibitorDOB')?.valueChanges
      .subscribe((value: Date | undefined) => {
        this.previousBirthDate = value;
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['registration']) {
      const registration = changes['registration'].currentValue as Registration;
      this.formGroup.patchValue(registration);
      this.chapters = this.counties.find(c => c.county == registration.county)?.chapters ?? [];
      this.previousBirthDate = registration.exhibitorDOB;
    }
  }

  getQualityCounts() {
    this.getRegistrationDetails();

    if (CommonUtils.isNotEmpty(this.registration.firstName)
      && CommonUtils.isNotEmpty(this.registration.lastName)
      && this.registration.exhibitorDOB != null) {

      this.registrationService.getQualityCounts(this.registration.firstName, this.registration.lastName, this.registration.exhibitorDOBStr).subscribe({
        next: qualityCounts => {
          this.registration.qualityCounts = {
            qcNumber: qualityCounts.qcNumber,
            currentGrade: qualityCounts.currentGrade,
            ffaCounty: qualityCounts.ffaCounty,
            level: qualityCounts.level,
            fourHCounty: qualityCounts.fourHCounty
          };

          if (this.registration.firstName
            && this.registration.lastName
            && this.registration.ageDiscloserRequired
            && !this.registration.ageDiscloserAccepted) {
            this.showAgeDisclosures();
          }

          this.notificationService.clear();

          this.formGroup.markAsUntouched();
          this.formGroup.updateValueAndValidity();
        },
        error: (err: HttpErrorResponse) => {
          if (err.status == 404) {
            this.showDialog = true;
          } else {
            this.registration.qualityCounts = undefined;
            this.notificationService.error(err);
          }

          this.formGroup.markAsUntouched();
          this.formGroup.updateValueAndValidity();
        }
      });
    }
  }

  resetQualityCounts() {
    this.registration.qualityCounts = {
      qcNumber: undefined,
      currentGrade: undefined,
      level: undefined
    };

    this.registration.ageDiscloserAccepted = false;
    this.registration.ageDiscloserRequired = false;

    this.showDialog = false;

    this.getRegistrationDetails();

    if (this.registration.firstName
      && this.registration.lastName
      && this.registration.ageDiscloserRequired
      && !this.registration.ageDiscloserAccepted) {
      this.showAgeDisclosures();
    }
  }

  getAddress() {
    /*this.getRegistrationDetails();

    if (CommonUtils.isNotEmpty(this.registration.address) &&
      CommonUtils.isNotEmpty(this.registration.city) &&
      CommonUtils.isNotEmpty(this.registration.state)) {

      this.registrationService.getAddress(this.registration.address, this.registration.city, this.registration.state).subscribe({
        next: address => {
          if (address) {
            this.registration.zipCode = address.zipCode;

            this.formGroup.patchValue(this.registration);

            this.notificationService.clear();
          } else {
            this.notificationService.warn({ message: 'We couldn\'t verify given address. Please check address details.' });
          }
        },
        error: err => {
          this.notificationService.warn({ message: 'We couldn\'t verify given address. Please check address details.' });
        }
      });
    }*/
  }

  validateForm(): boolean {
    this.formGroup.markAllAsTouched();
    this.formGroup.updateValueAndValidity();
    this.changeDetector.detectChanges();

    return this.formGroup.valid;
  }

  getRegistrationDetails(): Registration {
    const reg = new Registration(this.formGroup.value);
    this.registration.orgId = reg.orgId;
    this.registration.firstName = CommonUtils.toTitleCase(reg.firstName);
    this.registration.lastName = CommonUtils.toTitleCase(reg.lastName);
    this.registration.fullName = `${this.registration.firstName} ${this.registration.lastName}`;
    this.registration.exhibitorDOB = reg.exhibitorDOB;
    this.registration.exhibitorDOBStr = CommonUtils.formatDateToString(reg.exhibitorDOB);
    this.registration.address = reg.address;
    this.registration.city = CommonUtils.toTitleCase(reg.city);
    this.registration.state = reg.state;
    this.registration.zipCode = reg.zipCode;
    this.registration.phone = reg.phone;
    this.registration.email = reg.email;
    this.registration.county = reg.county;
    this.registration.chapter = reg.chapter;
    this.registration.ageDiscloserRequired = this.orgConfigs && this.registration.exhibitorDOB ? CommonUtils.calculateAge(this.registration.exhibitorDOB) < this.orgConfigs.registrationAgeLimit : false;

    return this.registration;
  }

  showAgeDisclosures() {
    this.confirmationService.confirm({
      header: 'Age Disclosure',
      message: 'Accept to affirm this information is being provided by the parent or legal guardian.',
      defaultFocus: 'none',
      icon: 'pi pi-info-circle',
      acceptButtonStyleClass: "p-button",
      rejectButtonStyleClass: "p-button-outlined p-button-danger",
      acceptLabel: 'Accept',
      rejectLabel: 'Reject',
      acceptIcon: "none",
      rejectIcon: "none",
      accept: () => {
        this.registration.ageDiscloserAccepted = true;
      }
    });
  }

  onInput = CommonUtils.onDateInput;

  onCountyChange(event: DropdownChangeEvent) {
    if (this.registration.liveStocks.length > 0) {
      this.confirmationService.confirm({
        header: 'County Change',
        message: 'Changing county will remove all selected Shows. Would you like to continue?',
        defaultFocus: 'none',
        icon: 'pi pi-info-circle',
        acceptButtonStyleClass: "p-button",
        rejectButtonStyleClass: "p-button-outlined p-button-danger",
        acceptLabel: 'Continue',
        rejectLabel: 'Cancel',
        acceptIcon: "none",
        rejectIcon: "none",
        accept: () => {
          this.registration.liveStocks = [];
          this.chapters = this.counties.find(c => c.county == event.value)?.chapters ?? [];
        },
        reject: () => {
          this.formGroup.controls['county'].setValue(this.registration.county);
        }
      });
    } else {
      this.chapters = this.counties.find(c => c.county == event.value)?.chapters ?? [];
    }
  }

}
